import { useTranslation } from "@bounce/i18n"

import type { StoreWithNickname } from "../types"

export type UseStoreNicknameHook = (params: {
  store: StoreWithNickname | null | undefined
  productSuffix?: "LUGGAGE_STORAGE" | "PACKAGE_ACCEPTANCE"
}) => string

type FormatNicknameOptions = {
  nickname: string
  language: string
}

/**
 * Remove the store nickname suffixes and prefixes
 * @example formatNickname({ nickname: "Near Louvre Storage Spot", language: "de-DE" }) // "Louvre"
 */
export const formatNickname = ({ nickname, language }: FormatNicknameOptions) => {
  // Remove product suffixes and prefixes
  let formattedNickname = nickname.replaceAll(/Storage Spot|Pickup Point/gi, "") || ""

  // Remove "Near" suffixes and prefixes in foreign languages
  if (language !== "en-US") {
    formattedNickname = formattedNickname.replaceAll(/Near/gi, "")
  }

  return formattedNickname.trim()
}

/**
 * @example
 * const nicknameWithProduct = useStoreNickname({ store, productSuffix: "LUGGAGE_STORAGE" }) // "Bounce Office Storage Point"
 * const nickname = useStoreNickname({ store }) // "Bounce Office"
 */
const useStoreNickname: UseStoreNicknameHook = ({ store, productSuffix }) => {
  const { t, i18n } = useTranslation("store")

  const nickname = formatNickname({ nickname: store?.nickname || "", language: i18n.language })

  if (store && store.type === "Locker") {
    return nickname
  }

  if (productSuffix === "LUGGAGE_STORAGE") {
    return t("hook.useStoreNickname.luggageStorageV2", {
      defaultValue: "{nickname} Storage Spot",
      nickname,
    })
  }

  if (productSuffix === "PACKAGE_ACCEPTANCE") {
    return t("hook.useStoreNickname.packageAcceptanceV2", {
      defaultValue: "{nickname} Pickup Point",
      nickname,
    })
  }

  return nickname.trim()
}

export { useStoreNickname }
